import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Send, Plus, Search, ChevronRight } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-50 text-blue-700',
  viewed: 'bg-purple-50 text-purple-700',
  completed: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-red-50 text-red-700',
  voided: 'bg-slate-100 text-slate-400',
  expired: 'bg-amber-50 text-amber-700',
};

const TABS = ['all', 'draft', 'sent', 'completed', 'declined'];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Envelopes() {
  const navigate = useNavigate();
  const [envelopes, setEnvelopes] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/envelopes')
      .then(r => setEnvelopes(r.data))
      .catch(() => toast.error('Failed to load envelopes'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = envelopes.filter(e => {
    const matchesSearch = e.title?.toLowerCase().includes(search.toLowerCase()) || e.document_name?.toLowerCase().includes(search.toLowerCase());
    const matchesTab = tab === 'all' || e.status === tab;
    return matchesSearch && matchesTab;
  });

  return (
    <div className="p-6 md:p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Envelopes</h1>
          <p className="text-slate-500 text-sm mt-0.5">{envelopes.length} total envelope{envelopes.length !== 1 ? 's' : ''}</p>
        </div>
        <Link
          to="/envelopes/create"
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
          data-testid="create-envelope-btn"
        >
          <Plus size={15} /> New Envelope
        </Link>
      </div>

      {/* Search & Tabs */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search envelopes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="envelope-search-input"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-sm p-1">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium capitalize transition-colors ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Send size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium">No envelopes {tab !== 'all' ? `with status "${tab}"` : ''}</p>
            <Link to="/envelopes/create" className="mt-3 inline-flex items-center gap-1.5 text-blue-700 text-sm hover:underline">
              <Plus size={14} /> Send your first document
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Recipients</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Created</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(env => (
                <tr
                  key={env.id}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/envelopes/${env.id}`)}
                  data-testid={`envelope-row-${env.id}`}
                >
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-slate-900 truncate max-w-xs">{env.title}</p>
                    <p className="text-xs text-slate-400 truncate">{env.document_name}</p>
                  </td>
                  <td className="px-4 py-3.5 text-slate-600 hidden sm:table-cell">
                    {env.recipients?.length || 0} signer{env.recipients?.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs px-2 py-1 rounded-sm font-medium uppercase tracking-wide ${STATUS_STYLES[env.status] || 'bg-slate-100 text-slate-600'}`}
                      data-testid={`envelope-status-${env.id}`}>
                      {env.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs hidden md:table-cell">{formatDate(env.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Link
                      to={`/envelopes/${env.id}`}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded"
                      data-testid={`view-envelope-${env.id}`}
                      onClick={e => e.stopPropagation()}
                    >
                      <ChevronRight size={16} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
